import { StyledToggleButton } from '@/components/MultipleSelectFilter';
import { colors, mediaQueries } from '@/theme/lib';
import { KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type FilterProps = {
  title: string;
  options: { label: string, value: string }[];
  selected: string | null;
  onChange: (option: string) => void;
};

type StyledListProps = {
  isOpen: boolean;
};

const StyledToggleButtonWrapper = styled.div`
  div {
    display: inline-block;
  }
`;

const StyledList = styled.ul.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen"
})<StyledListProps>`
  background: ${colors.white};
  border-radius: 0.3rem;
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  padding: 0.75rem 0;
  margin: 0.25rem 0 0.5rem;
  min-width: 13rem;

  ${mediaQueries.md} {
    display: ${(props) => (props.isOpen ? 'inline-flex' : 'none')};
    width: auto;
    position: absolute;
    z-index: 999;
  }
`;

type StyledListItemProps = {
  selected: boolean;
};
const StyledListItem = styled.li<StyledListItemProps>`
  list-style: none;
  cursor: pointer;
  background: ${(props) => props.selected && props.theme.colors.tertiary};
  color: ${(props) => props.selected && props.theme.key === 'whipsnade' && colors.white};
  padding: 0.75rem 1.5rem;
`;

// See https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html for guide on accessible customised select component.
export default function SingleSelectFilter({
  title,
  options,
  selected,
  onChange,
}: FilterProps): ReactElement<any> {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const onOptionClick = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleButtonKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault();
        toggleOpen();
        break;
      default:
        break;
    }
  };

  const handleOptionKeyDown = (e: KeyboardEvent, option: string) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault();
        onOptionClick(option);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef}>
      <div>
        <StyledToggleButtonWrapper>
          <StyledToggleButton
            onClick={toggleOpen}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="listbox1"
            role="combobox"
            isOpen={isOpen}
            as="div"
            aria-label={title}
            tabIndex={0}
            onKeyDown={handleButtonKeyDown}
          >
            {options.find((o) => o.value === selected)?.label ?? title}
          </StyledToggleButton>
        </StyledToggleButtonWrapper>
        <StyledList
          tabIndex={-1}
          role="listbox"
          id="listbox1"
          aria-activedescendant={`option-${selected}`}
          isOpen={isOpen}
        >
          {options.map((option) => (
            <StyledListItem
              tabIndex={0}
              key={option.value}
              id={`option-${option.value}`}
              selected={option.value === selected}
              aria-selected={option.value === selected}
              role="option"
              onClick={() => onOptionClick(option.value)}
              onKeyDown={(event: KeyboardEvent) => handleOptionKeyDown(event, option.value)}
            >
              {option.label}
            </StyledListItem>
          ))}
        </StyledList>
      </div>
    </div>
  );
}
